// @flow
import React, { PureComponent, Fragment } from 'react';
import { Link } from 'react-router-dom';

import TeamFilter from 'containers/team/TeamFilterContainer';
import RencontreClubs from 'components/fragments/rencontre/RencontreClubs';
import Loading from 'components/fragments/Loading';
import { withHelmet } from 'infrastructure/seo/Helmet';

import { formatFullDateString } from 'utils/dateUtils';
import { getMetaBySlug } from 'utils/seoUtils';
import { getPublicAssets } from 'utils/assetsUtils';

import type { PageListType } from 'types/PageList';
import type { CompetitionResultType, JourneeResultType, ResultType } from 'types/Result';
import type { RouterProps } from 'types/Router';
import type { Status } from 'types/Status';
import { type TeamStatsType } from 'types/TeamStats';

import { STATUS_DEFAULT, STATUS_LOADING } from 'constants/statusConstants';

export type StateProps = {
  competitionIds: number[],
  title: string,
  clubId: string,
  clubName: string,
  competitions: CompetitionResultType[],
  status: Status,
  rejoignez_nous: string,
  rejoignez_nous_background: string,
  historique: $PropertyType<PageListType, 'competitions_saisons'>,
  historiqueResults: {
    [key: string]: {
      status: Status,
      competitions: CompetitionResultType[],
      teamStats: TeamStatsType,
    },
  },
  currentSeason: { id: string, nom: string },
  seasons: { id: string, nom: string }[],
};

export type DispatchProps = {
  fetchTeamResultStart: (clubId: string, competitions: string[] | null, season?: string) => void,
}

type Props = DispatchProps & StateProps & RouterProps;

type State = {
  selectedCompetitions: number[],
  stateDerivedFromProps: boolean,
  selectedSeason: string,
};

class TeamResult extends PureComponent<Props, State> {
  state = {
    selectedCompetitions: [],
    stateDerivedFromProps: false,
    selectedSeason: '',
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const { selectedCompetitions, stateDerivedFromProps, selectedSeason } = prevState;
    const { historique, currentSeason } = nextProps;

    const data = historique[selectedSeason || currentSeason.id];

    // $FlowFixMe
    if (!stateDerivedFromProps && selectedCompetitions.length < 1 && data?.competitions?.length) {
      // $FlowFixMe
      return { selectedCompetitions: data.competitions.map(({ id }) => parseInt(id, 10)), stateDerivedFromProps: true };
    }

    return null;
  }

  _renderRencontres = (rencontres: ResultType[]) => {
    const { match: { params: { slug } } } = this.props;

    return rencontres && rencontres.map < React$Node > (({
      id,
      localStructure, localStructure: { nom: localNom },
      visiteurStructure, visiteurStructure: { nom: visiteurNom },
      resultLocal: { pointsDeMarque: pointLocal },
      resultVisiteur: { pointsDeMarque: pointVisiteur },
    }) => {

      return (
        <Link
          key={id}
          className="match box box--white mb-2"
          to={`/equipes/${slug || ''}/match/${id}`}
          title={`Voir les informations du match ${localNom} - ${visiteurNom}`}
        >
          <RencontreClubs
            localStructure={localStructure}
            visiteurStructure={visiteurStructure}
            pointLocal={pointLocal}
            pointVisiteur={pointVisiteur}
          />
        </Link>
      );
    });
  };

  _renderJournees = (jours: JourneeResultType[]) => {
    return jours && jours.map < React$Node > ((jour: JourneeResultType) => (
      <Fragment key={jour.id}>
        <p className="block-competition__head ft-h6 ft-up mb-1">{jour.nom}  —  {formatFullDateString(jour.date)}</p>
        {this._renderRencontres(jour.rencontres)}
      </Fragment>
    ));
  };

  _renderCompetitions = (): React$Node => {
    const { competitions, currentSeason, historiqueResults } = this.props;
    const { selectedCompetitions, selectedSeason } = this.state;

    let compets;
    if (!selectedSeason || selectedSeason === currentSeason.id.toString()) {
      compets = competitions;
    } else {
      const histo = historiqueResults[selectedSeason];

      if (histo) {
        if (histo.status === STATUS_LOADING || histo.status === STATUS_DEFAULT) {
          return (
            <div className="placeholder col col-lg-8on9" style={{ justifyContent: 'center' }}>
              <Loading />
            </div>
          );
        }

        compets = histo.competitions;
      } else {
        compets = [];
      }
    }


    const filtererdCompetitions = compets.filter(({ id }) => selectedCompetitions.includes(parseInt(id, 10)));

    if (filtererdCompetitions && filtererdCompetitions.length > 0) {
      return filtererdCompetitions.map((competition): React$Node => {
        return (
          <div className="row mt-5" key={competition.id}>
            <div className="col col-lg-9on9">
              <h3 className="mb-3">{competition.nom}</h3>
              {competition.journees && this._renderJournees(competition.journees)}
              <hr className="separator display-desktop" />
              <a
                href={`https://competitions.ffr.fr/${competition.saison.nom}/competitions/${competition.identifiant}/resultats.html`}
                target="_blank"
                rel="noopener nofollow noreferrer"
                className="link-chevron link-chevron--blue link-chevron--inline link-chevron--external"
              >
                <span>Voir les résultats de la compétition</span>
                <i className="icon icon-external" />
              </a>
            </div>
          </div>
        );
      });
    }

    return (
      <div className="placeholder col col-lg-8on9">
        <p className="ft-h5">
          <i className="icon " />
          Aucune rencontre pour cette recherche
        </p>
      </div>
    );
  };

  _renderSidebar = () => {
    const {
      rejoignez_nous,
      rejoignez_nous_background
    } = this.props;

    const background = rejoignez_nous_background ? rejoignez_nous_background : getPublicAssets('/img/trash/card.jpg');

    return (
      <div className="col col-md-4 col-lg-3">
        <aside className="sidebar">
          {rejoignez_nous &&
            <Link
              to={rejoignez_nous}
              title="Voir les modalités d'inscription"
              className="box card card--content mb-4"
            >
              <div
                className="card__thumbnail img-blue"
                style={{ backgroundImage: `url(${background})` }}
              >
                <img src={background} alt='' />
              </div>
              <div className="card__body">
                <h3 className="card__title">Rejoignez-nous !</h3>
                <p className="card__desc"></p>
              </div>
              <div className="card__footer">
                <span className="link-chevron ">
                  Consulter
                  <i className="icon icon-chevron-right" />
                </span>
              </div>
            </Link>}
        </aside>
      </div>
    );
  };

  handleCompetitionChange = (selectedCompetitions: number[]) => {
    this.setState({ selectedCompetitions });
  };

  onSeasonChange = (v: string) => {
    const value = v.toString();
    const { selectedSeason } = this.state;
    const { clubId, historique, fetchTeamResultStart } = this.props;

    if (value === selectedSeason) {
      return;
    }

    let compets: string[] = [];
    const histo = historique[value];

    // $FlowFixMe
    if (histo?.competitions) {
      // $FlowFixMe
      compets = histo.competitions.map(({ id }) => id);
    }

    fetchTeamResultStart(clubId, compets, value);
    this.setState({ selectedSeason: value, selectedCompetitions: compets.map((item) => parseInt(item, 10)) });
  };

  getCurrentSeasonData = () => {
    const { historique, currentSeason } = this.props;
    const { selectedSeason } = this.state;

    return historique[selectedSeason || currentSeason.id];
  };

  render() {
    const { title, clubName, status, historique, seasons, currentSeason } = this.props;
    const { selectedCompetitions, selectedSeason } = this.state;

    if (status === STATUS_LOADING) {
      return <Loading />;
    }

    const filterSeasons = seasons.filter(({ id }) =>
      // $FlowFixMe
      id === currentSeason.id || historique[id.toString()]?.competitions?.length
    );
    const data = this.getCurrentSeasonData();

    return (
      <div className="container">
        <div className="row">
          <div className="col col-md-8 col-lg-9 bg-overflow">
            <section>
              <h2 className='mt-3'>{`Résultats de l'équipe ${title} de ${clubName}`}</h2>

              <TeamFilter
                className='header-filter header-filter--team mt-2 mt-lg-8'
                // $FlowFixMe
                competitions={data?.competitions ?? []}
                onCompetitionChange={this.handleCompetitionChange}
                selectedCompetitions={selectedCompetitions}
                onSeasonChange={this.onSeasonChange}
                withLink
                withCompetition
                withSeason
                seasons={filterSeasons}
                selectedSeason={selectedSeason || currentSeason.id.toString()}
              />

              <div className="row">
                <div className="col col-lg-8on9">
                  <div className="block-competition mb-md-4 mb-lg-0">
                    {this._renderCompetitions()}
                  </div>
                </div>
              </div>
            </section>
          </div>
          {this._renderSidebar()}
        </div>
      </div>
    );
  }
}

export default withHelmet(TeamResult)((Helmet, pageProps) => {
  const { match: { params, url }, title, clubName } = pageProps;

  const meta = getMetaBySlug(url, params);

  return (
    <Helmet>
      <title>{meta.title(title, clubName)}</title>
      {
        meta.description()
          ? <meta name={'description'} content={meta.description()} />
          : ''
      }
      <meta name={'keywords'} content={meta.keywords(title, clubName)} />
      <meta property={'og:title'} content={meta.title(title, clubName)} />
      {
        meta.description()
          ? <meta property={'og:description'} content={meta.description()} />
          : ''
      }
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
